import { ChevronDownIcon } from "@heroicons/react/outline";
import { ReactNode } from "react";

import HookCollapsibleContent from "./HookCollapsibleContent";

import { Collapsible, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";

interface HookCollapsibleSectionProps {
  title: string;
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  headerAction?: ReactNode;
  children: ReactNode;
}

const HookCollapsibleSection = ({
  title,
  isExpanded,
  onExpandedChange,
  headerAction,
  children,
}: HookCollapsibleSectionProps) => (
  <Collapsible open={isExpanded} onOpenChange={onExpandedChange}>
    <div className="rounded-lg border border-gray-200 transition-all duration-200">
      <div className="flex items-center border-b border-gray-200/50 hover:bg-gray-50/30">
        <CollapsibleTrigger asChild>
          <button type="button" className="min-w-0 flex-1 px-4 py-3 text-left">
            <h3 className="truncate text-sm font-medium text-gray-900">{title}</h3>
          </button>
        </CollapsibleTrigger>
        <div className="flex shrink-0 items-center gap-2 pr-4">
          {headerAction}
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:text-gray-600"
              aria-label={isExpanded ? "Collapse hook" : "Expand hook"}
            >
              <ChevronDownIcon
                className={cn(
                  "h-4 w-4 transition-transform duration-300 ease-in-out",
                  isExpanded && "rotate-180",
                )}
              />
            </button>
          </CollapsibleTrigger>
        </div>
      </div>

      <HookCollapsibleContent>
        <div className="space-y-4 p-4">{children}</div>
      </HookCollapsibleContent>
    </div>
  </Collapsible>
);

export default HookCollapsibleSection;
